// @ts-nocheck
// KPI row for a live-calls board. Same tile as cti2/team/TeamKpiStrip (not
// exported there) so the admin page and the team board match. Clicking a
// phase tile filters the table below to that phase.
import React from 'react';
import { HAIRLINE, TONE, nf } from '@/features/cti2/team/teamUi';
import type { LiveCallCounts } from '../types';
import type { LiveCallCounters, PhaseFilter } from '../liveCallsModel';

function KpiTile({ label, value, sub, tone, active, onClick }) {
  const toneClass = tone ? TONE[tone].text : 'text-foreground';
  const interactive = typeof onClick === 'function';
  return (
    <button
      type="button"
      disabled={!interactive}
      onClick={onClick}
      className={`rounded-2xl border bg-card p-3 text-left shadow-sm transition-colors ${HAIRLINE} ${
        interactive ? 'hover:bg-muted/40' : 'cursor-default'
      } ${active ? 'ring-2 ring-primary/40' : ''}`.trim()}
    >
      <div className="truncate text-[10px] font-semibold uppercase tracking-[0.08em] text-muted-foreground">{label}</div>
      <div className={`mt-1.5 truncate text-[22px] font-semibold leading-none tracking-tight tabular-nums ${toneClass}`}>
        {nf(value)}
      </div>
      {sub ? <div className="mt-1.5 truncate text-[10px] text-muted-foreground">{sub}</div> : null}
    </button>
  );
}

interface Props {
  counters: LiveCallCounters;
  /** ZCARD totals for the whole call centre, when the scope has them. */
  counts?: LiveCallCounts | null;
  phase: PhaseFilter;
  onPhase: (next: PhaseFilter) => void;
  compact?: boolean;
}

export default function LiveCallsKpiStrip({ counters, counts, phase, onPhase, compact = false }: Props) {
  const total = counts ? counts.total : counters.total;
  const inOut = counts
    ? `${nf(counts.in)} in · ${nf(counts.out)} out · ${nf(counts.int)} int`
    : `${nf(counters.IN)} in · ${nf(counters.OUT)} out · ${nf(counters.INT)} int`;
  const tiles = [
    { key: 'total', label: 'Live calls', value: total, sub: inOut },
    { key: 'talking', label: 'Talking', value: counters.talking, tone: 'good', sub: 'Connected', phase: 'talking' },
    { key: 'waiting', label: 'Waiting', value: counters.waiting, tone: counters.waiting > 0 ? 'warn' : undefined, sub: 'Queue · IVR', phase: 'waiting' },
    { key: 'ringing', label: 'Ringing', value: counters.ringing, tone: 'info', sub: 'Setup → ringing', phase: 'ringing' },
    { key: 'hold', label: 'On hold', value: counters.hold, tone: counters.hold > 0 ? 'warn' : undefined, sub: 'Customer holding', phase: 'hold' },
  ];
  if (!compact) {
    tiles.push({ key: 'transfers', label: 'Transfers', value: counters.transfers, sub: 'In progress' });
    tiles.push({ key: 'snooped', label: 'Monitored', value: counters.snooped, sub: 'Leads listening' });
  }
  const cols = compact ? 'grid-cols-2 sm:grid-cols-5' : 'grid-cols-2 sm:grid-cols-4 xl:grid-cols-7';
  return (
    <div className={`grid gap-2.5 ${cols}`}>
      {tiles.map((t) => (
        <KpiTile
          key={t.key}
          label={t.label}
          value={t.value}
          sub={t.sub}
          tone={t.tone}
          active={t.phase ? phase === t.phase : false}
          onClick={t.phase ? () => onPhase(phase === t.phase ? 'all' : t.phase) : undefined}
        />
      ))}
    </div>
  );
}
